import { CSSProperties, FC } from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { TPersonData, cn, FaIcon } from '@fe-learner-common';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';

export type AvatarUserProps = {
  src?: string;
  alt: string;
  firstName?: string;
  lastName?: string;
  online?: TPersonData['status'];
};

export type AvatarComponentProps = {
  size: 'xs' | 'md' | 'lg' | 'xl' | '2xl';
};

type AvatarProps = AvatarUserProps &
  AvatarComponentProps & {
    className?: string;
    style?: CSSProperties;
  };

const sizeMap = {
  xs: 'lf:w-5 lf:h-5 lf:text-label-sm lf:text-[8px]',
  md: 'lf:w-6 lf:h-6 lf:text-label-sm lf:text-[9px]',
  lg: 'lf:w-8 lf:h-8 lf:text-label-sm',
  xl: 'lf:w-10 lf:h-10 lf:text-label-md',
  '2xl': 'lf:w-[72px] lf:h-[72px] lf:text-label-lg',
};

const getInitials = (firstName?: string, lastName?: string) => {
  if (!firstName || !lastName) return null;

  return `${firstName[0]}${lastName[0]}`;
};

export const Avatar: FC<AvatarProps> = ({
  className,
  style,
  alt,
  src,
  size,
  firstName,
  lastName,
  online,
  ...rest
}) => {
  if (!src) {
    return (
      <AvatarPlaceholder
        ariaLabel={alt}
        size={size}
        className={className}
        style={style}
      />
    );
  }

  return (
    <AvatarPrimitive.Root
      style={style}
      className={cn(
        'lf:relative lf:inline-flex lf:select-none lf:items-center lf:justify-center lf:rounded-full lf:align-middle',
        sizeMap[size],
        className,
      )}
      {...rest}
    >
      <AvatarPrimitive.Image
        className="lf:h-full lf:w-full lf:rounded-[inherit] lf:object-cover"
        src={src}
        alt={alt}
      />
      <AvatarPrimitive.Fallback
        className={cn(
          'lf:flex lf:h-full lf:w-full lf:animate-pulse lf:items-center lf:justify-center lf:rounded-full lf:bg-neutral-300 lf:text-neutral-0',
        )}
      >
        {getInitials(firstName, lastName)}
      </AvatarPrimitive.Fallback>
      {online === 'online' && (
        <span
          className={cn(
            'lf:absolute lf:bottom-0 lf:right-0 lf:z-10 lf:h-2 lf:w-2 lf:rounded-xl lf:bg-positive-500 lf:outline lf:outline-2 lf:outline-neutral-0 lf:group-hover:outline-neutral-100',
          )}
        ></span>
      )}
    </AvatarPrimitive.Root>
  );
};

export type AvatarPlaceholderProps = {
  ariaLabel: string;
  className?: string;
  style?: CSSProperties;
} & AvatarComponentProps;

const iconSizeMap = {
  xs: 10,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 36,
};

export const AvatarPlaceholder: FC<AvatarPlaceholderProps> = ({
  ariaLabel,
  size,
  className,
  style,
}) => {
  const iconSize = iconSizeMap[size];

  return (
    <AvatarPrimitive.Root
      aria-label={ariaLabel}
      style={style}
      className={cn(
        'lf:inline-flex lf:items-center lf:justify-center lf:rounded-full',
        sizeMap[size],
        className,
      )}
    >
      <AvatarPrimitive.Fallback className="lf:flex lf:h-full lf:w-full lf:items-center lf:justify-center lf:rounded-full lf:bg-neutral-200">
        <FaIcon
          icon={faUser}
          className="lf:text-neutral-550"
          width={iconSize}
          height={iconSize}
        />
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
};
